import { useTranslate } from "@refinedev/core";
import { useMemo } from "react";
import { useThemeBulletPoints } from "pages/media/components/themeEditor/ThemeBulletPointsContext";
import { Slide } from "pages/media/types/";
import { PanelSection } from "../PanelSection";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useSaveSlidesAsset } from "../../../hooks/useSaveSlidesAsset";
import { Table, TableDividerStyle, TableTextStyle } from "../../../types";
import { ItemAlignmentFeature } from "../../features/ItemAlignmentFeature";
import { TableBulletPointsContentFeature } from "./features/TableBulletPointsContentFeature";
import { TableThemePlaceholdersFeature } from "./features/TableThemePlaceholdersFeature";
import { TableTextStyleFeature } from "./features/TableTextStyleFeature";
import { TableDividerStyleFeature } from "./features/TableDividerStyleFeature";

export const TableTab = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();
  const themeBulletPoints = useThemeBulletPoints();

  const {
    organizationId,
    projectId,
    templatesData: { storyboard },
    storyboardWithSlidesData: { slides, setSlide },
    brandKitData: {
      titleFont,
      textFont,
      subtitlesFont,
      brandKitPalette: palette,
    },
  } = useTemplateData();

  const currentSlide = useTemplateEditorStore((state) => state.currentSlide);

  const { saveSlidesAsset } = useSaveSlidesAsset({ organizationId, projectId });

  const currentSlideData = useMemo(
    () => slides.find((s) => s.uuid === currentSlide),
    [slides, currentSlide]
  );

  const bulletPoints =
    currentSlideData && "bullet_points" in currentSlideData.placeholders
      ? currentSlideData.placeholders.bullet_points
      : undefined;

  const brandKitFonts = useMemo(
    () =>
      [titleFont, textFont, subtitlesFont].filter(
        (asset) => asset !== undefined
      ),
    [titleFont, textFont, subtitlesFont]
  );

  const { table, updateTable, updateTableTextStyle, updateTableDividerStyle } =
    useTemplateEditorStore((state) => ({
      table: state.templateState.table,
      updateTable: state.updateTable,
      updateTableTextStyle: state.updateTableTextStyle,
      updateTableDividerStyle: state.updateTableDividerStyle,
    }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  if (!table) {
    return (
      <PanelSection
        title={t("components.templates.settings.tableTab.tabTitle")}
        subtitle={t("components.templates.settings.tableTab.noTable")}
        primary
      />
    );
  }

  const handleTableChange = (update: Partial<Table>) => {
    updateTable(update);
    addTemplateUpdate();
  };

  const handleTextStyleChange = (style: Partial<TableTextStyle>) => {
    updateTableTextStyle(style);
    addTemplateUpdate();
  };

  const handleDividerStyleChange = (style: Partial<TableDividerStyle>) => {
    updateTableDividerStyle(style);
    addTemplateUpdate();
  };

  const persistBulletPoints = (newBulletPoints: string[]) => {
    if (!currentSlideData?.uuid) return;

    const updatedSlide = {
      ...currentSlideData,
      placeholders: {
        ...currentSlideData.placeholders,
        bullet_points: newBulletPoints,
      },
    } as Slide;

    setSlide(currentSlideData.uuid, updatedSlide);

    if (storyboard?.slides_asset_id) {
      const allSlidesToSave = slides.map((s) =>
        s.uuid === currentSlideData.uuid ? updatedSlide : s
      );
      // No explicit out-of-date mark: BE already flags the media on every
      // Slides asset PATCH (it only skips Storyboard ones).
      void saveSlidesAsset(storyboard.slides_asset_id, allSlidesToSave).catch(
        (error) => {
          console.error("Error saving bullet points", error);
        }
      );
    }
  };

  return (
    <>
      <PanelSection
        title={t("components.templates.settings.tableTab.tabTitle")}
        subtitle={t("components.templates.settings.tableTab.description")}
        primary
        noBorder
      />

      {bulletPoints && currentSlideData?.uuid && (
        <PanelSection
          title={t(
            "components.templates.settings.tableTab.bulletPointsContent"
          )}
          noBorder
        >
          <TableBulletPointsContentFeature
            key={currentSlideData.uuid}
            initialBulletPoints={bulletPoints}
            onChange={persistBulletPoints}
          />
        </PanelSection>
      )}

      {themeBulletPoints && (
        <PanelSection
          title={t("components.templates.settings.tableTab.tablePlaceholders")}
          noBorder
        >
          <TableThemePlaceholdersFeature
            count={themeBulletPoints.count}
            onCountChange={themeBulletPoints.setCount}
            baseText={themeBulletPoints.baseText}
            onBaseTextChange={themeBulletPoints.setBaseText}
          />
        </PanelSection>
      )}

      <PanelSection
        title={t("components.templates.settings.tableTab.tableStyle")}
        noBorder
      >
        <ItemAlignmentFeature
          pickedItem={table}
          onChange={(value) => handleTableChange(value as Partial<Table>)}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      </PanelSection>

      <PanelSection
        title={t("components.templates.settings.tableTab.textStyle")}
        noBorder
      >
        <TableTextStyleFeature
          textStyle={table.textStyle}
          palette={palette}
          organizationId={organizationId}
          brandKitFonts={brandKitFonts}
          titleFont={titleFont}
          textFont={textFont}
          subtitlesFont={subtitlesFont}
          onChange={handleTextStyleChange}
        />
      </PanelSection>

      <PanelSection
        title={t("components.templates.settings.tableTab.dividerStyle")}
        noBorder
      >
        <TableDividerStyleFeature
          table={table}
          palette={palette}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          onUpdateTable={handleTableChange}
          onChange={handleDividerStyleChange}
        />
      </PanelSection>
    </>
  );
};
